<template>
  <!--
    Middle-level "section" inside an expanded company panel. The
    legacy "Row" name is kept to avoid renaming imports — it's no
    longer a table row. The make header is a compact clickable bar:
    name + count side-by-side, not on opposite ends of the bar (the
    earlier layout left a huge empty gap between them).

    When expanded, a fixed-layout <table> renders the PMAs. The
    colgroup pins widths so every make's PMA list inside the same
    company panel shares the exact same column layout.
  -->
  <div>
    <button
      type="button"
      @click="toggleExpanded"
      class="w-full flex items-center px-2 py-1.5 hover:bg-white transition-colors text-left group"
      :aria-expanded="expanded"
    >
      <span class="text-gray-500 text-xs w-3 inline-block leading-none">
        {{ expanded ? "▼" : "▶" }}
      </span>
      <span class="ml-2 text-sm font-medium text-black group-hover:text-accent-primary">
        {{ make.make }}
      </span>
      <span class="ml-2 text-xs text-gray-500">·</span>
      <span class="ml-2 text-xs text-gray-600">
        {{ make.pma_count }} PMA<span v-if="make.pma_count !== 1">s</span>
      </span>
    </button>

    <!-- PMA table — lighter header (no dark bg), title attrs on truncated cells. -->
    <div v-if="expanded" class="bg-white border-y border-gray-200">
      <div v-if="pmasLoading" class="px-3 py-2 text-xs text-gray-600">
        Loading PMAs…
      </div>
      <div v-else-if="pmas.length === 0" class="px-3 py-2 text-xs text-gray-600">
        No PMAs found for this make under the current filters.
      </div>
      <div v-else>
        <table class="w-full table-fixed text-xs">
          <colgroup>
            <col style="width: 16rem" />
            <col style="width: 22rem" />
            <col style="width: 7rem" />
            <col />
            <col style="width: 5rem" />
          </colgroup>
          <thead>
            <tr class="text-gray-600 border-b border-gray-200">
              <th class="px-3 py-1.5 text-left font-semibold">Model</th>
              <th class="px-3 py-1.5 text-left font-semibold">Part Name</th>
              <th class="px-3 py-1.5 text-left font-semibold">Supplement Date</th>
              <th class="px-3 py-1.5 text-left font-semibold">FAA Approval Basis</th>
              <th class="px-3 py-1.5 text-left font-semibold"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <PMARow
              v-for="approval in pmas"
              :key="approval.id || `${approval.company_name}-${approval.supplement_number}-${approval.part_name}`"
              :approval="approval"
              :resetObject="resetObject"
            ></PMARow>
          </tbody>
        </table>

        <!--
          Mini paginator. Only rendered when this (company, make) bucket
          needs more than one page of PMAs.
        -->
        <div
          v-if="numPages > 1"
          class="flex items-center justify-end space-x-3 px-3 py-1.5 text-xs text-gray-700 border-t border-gray-100 bg-gray-50"
        >
          <button
            type="button"
            @click="goToPrev"
            :disabled="!prev"
            class="text-indigo-600 hover:underline disabled:text-gray-400 disabled:no-underline"
          >
            ← Prev
          </button>
          <div class="text-gray-600">
            Page {{ pageNumber }} / {{ numPages }} ({{ totalCount }} PMAs)
          </div>
          <button
            type="button"
            @click="goToNext"
            :disabled="!next"
            class="text-indigo-600 hover:underline disabled:text-gray-400 disabled:no-underline"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, ref, watch } from "vue";
import { useStore } from "vuex";
import { getPMACompanyMakePMAs } from "@/services/PMAList.js";
import PMARow from "./PMARow.vue";

const props = defineProps({
  make: { type: Object, required: true },
  companyName: { type: String, required: true },
  filterQuery: { type: String, default: "?" },
  resetObject: { type: Object, required: true },
});
const store = useStore();

const expanded = ref(false);
const pmas = ref([]);
const pmasLoaded = ref(false);
const pmasLoading = ref(false);
const pageNumber = ref(1);
const numPages = ref(1);
const next = ref(null);
const prev = ref(null);
const totalCount = ref(0);

function buildPMAsEndpoint(extra = "") {
  const base = props.filterQuery || "?";
  const joiner = base === "?" ? "" : "&";
  const params = [
    `selected_company_name=${encodeURIComponent(props.companyName)}`,
    `selected_make=${encodeURIComponent(props.make.make)}`,
  ].join("&");
  return `${base}${joiner}${params}${extra}`;
}

async function fetchPMAs(extra = "") {
  pmasLoading.value = true;
  try {
    const response = await getPMACompanyMakePMAs({
      token: store.state.auth.token,
      endpoint: buildPMAsEndpoint(extra),
    });
    pmas.value = response.approvals;
    totalCount.value = response.count || pmas.value.length;
    pageNumber.value = response.page || 1;
    numPages.value = response.num_pages || 1;
    next.value = response.next ? `&page=${response.page + 1}` : null;
    prev.value = response.prev ? `&page=${response.page - 1}` : null;
    pmasLoaded.value = true;
  } catch (err) {
    console.error("Failed to load PMAs for", props.companyName, props.make.make, err);
    pmas.value = [];
  } finally {
    pmasLoading.value = false;
  }
}

async function toggleExpanded() {
  expanded.value = !expanded.value;
  if (expanded.value && !pmasLoaded.value) {
    await fetchPMAs();
  }
}

function goToNext() {
  if (next.value) fetchPMAs(next.value);
}

function goToPrev() {
  if (prev.value) fetchPMAs(prev.value);
}

watch(
  () => props.resetObject.buttonHit,
  () => {
    expanded.value = false;
    pmas.value = [];
    pmasLoaded.value = false;
    pageNumber.value = 1;
    numPages.value = 1;
    next.value = null;
    prev.value = null;
    totalCount.value = 0;
  }
);
</script>
